import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Stack,
  CircularProgress,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { AutoFixHigh as NormalizeIcon, UploadFile as UploadIcon, TextFields as TextIcon, Download as DownloadIcon } from '@mui/icons-material';
import Header from './components/Header';
import FileUpload from './components/FileUpload';
import TextInput from './components/TextInput';
import AddressTable from './components/AddressTable';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import axios, { AxiosResponse } from 'axios';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import { API_ENDPOINTS } from './config';
import * as XLSX from 'xlsx';
import { message } from 'antd';
import Admin from './pages/Admin';
import Payment from './pages/Payment';

interface NormalizedAddress {
  originalAddress: string;
  city: string;
  district: string;
  commune: string;
  newCity?: string;
  newCommune?: string;
}

interface NormalizationResponse {
  queueId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
  result?: NormalizedAddress[];
}

interface NormalizationStatus {
  queueId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: NormalizedAddress[];
  error?: string;
  userGreenCoins?: number;
  userGoldCoins?: number;
}

const BATCH_SIZE = 20;
const POLLING_INTERVAL = 2000; // 2 seconds

const Home: React.FC = () => {
  const theme = useTheme();
  const [inputMethod, setInputMethod] = useState<'file' | 'text'>('file');
  const [addresses, setAddresses] = useState<NormalizedAddress[]>([]);
  const [isNormalizing, setIsNormalizing] = useState(false);
  const { user, logout, updateUserCoins } = useAuth();
  const addressTableRef = useRef<{ fetchAddresses: () => void }>(null);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const [currentBatchQueueId, setCurrentBatchQueueId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldStartNormalization, setShouldStartNormalization] = useState(false);
  const [hasNormalized, setHasNormalized] = useState(false);
  const [pendingAddresses, setPendingAddresses] = useState<NormalizedAddress[]>([]);
  const [isReadyToNormalize, setIsReadyToNormalize] = useState(false);

  const handleInputMethodChange = (
    event: React.MouseEvent<HTMLElement>,
    newMethod: 'file' | 'text' | null,
  ) => {
    if (newMethod !== null) {
      setInputMethod(newMethod);
    }
  };

  const handleUploadComplete = (data: any[]) => {
    const newAddresses = data.map(addr => ({
      originalAddress: addr.originalAddress || addr,
      city: '',
      district: '',
      commune: '',
      newCity: '',
      newCommune: ''
    }));
    setPendingAddresses(newAddresses);
    setAddresses(newAddresses);
    setIsReadyToNormalize(true);
  };

  const handleTextInputComplete = (inputAddresses: string[]) => {
    const newAddresses = inputAddresses.map(addr => ({
      originalAddress: addr,
      city: '',
      district: '',
      commune: '',
      newCity: '',
      newCommune: ''
    }));
    setPendingAddresses(newAddresses);
    setAddresses(newAddresses);
    setIsReadyToNormalize(true);
  };

  const handleNormalizeClick = async () => {
    if (addresses.length === 0) {
      message.warning('Vui lòng nhập địa chỉ cần chuẩn hóa');
      return;
    }
    // Check green coins for Google Gemini (default)
    if (!user || user.greenCoins <= 0) {
      message.warning('Bạn đã hết coin xanh để thực hiện chuẩn hóa. Vui lòng nạp thêm coins.');
      return;
    }

    // Always show confirmation dialog before starting normalization
    setConfirmDialogOpen(true);
  };

  const handleConfirmNormalize = async () => {
    setConfirmDialogOpen(false);
    setIsProcessingBatch(false);
    setCurrentBatchIndex(0);
    setHasNormalized(true);

    // Xóa bảng địa chỉ
    setAddresses([]);

    // Thêm địa chỉ mới vào bảng và bắt đầu chuẩn hóa
    setTimeout(() => {
      setAddresses(pendingAddresses);
      setShouldStartNormalization(true);
      setPendingAddresses([]);
    }, 0);
  };

  // New effect to handle normalization start
  useEffect(() => {
    if (shouldStartNormalization) {
      setIsNormalizing(true);
      processNextBatch();
      setShouldStartNormalization(false);  // Reset the trigger
    }
  }, [shouldStartNormalization, currentBatchIndex]);

  const handleCancelNormalize = () => {
    setConfirmDialogOpen(false);
    // Reset normalization states when canceling
    setIsNormalizing(false);
    setCurrentBatchIndex(0);
    setCurrentBatchQueueId(null);
    setIsProcessingBatch(false);
    setShouldStartNormalization(false);  // Reset the trigger
  };

  const processNextBatch = async () => {
    console.log('Processing next batch:', {
      currentBatchIndex,
      totalAddresses: addresses.length,
      isProcessingBatch
    });

    // Check if we've already processed all addresses
    if (currentBatchIndex >= addresses.length) {
      console.log('All addresses processed, stopping normalization');
      setIsNormalizing(false);
      setCurrentBatchIndex(0);
      setCurrentBatchQueueId(null);
      setIsProcessingBatch(false);
      return;
    }

    // Don't process if we're already processing a batch
    if (isProcessingBatch) {
      console.log('Already processing a batch, skipping');
      return;
    }

    setIsProcessingBatch(true);
    const startIndex = currentBatchIndex;
    const endIndex = Math.min(startIndex + BATCH_SIZE, addresses.length);
    const batchAddresses = addresses.slice(startIndex, endIndex).map(addr => addr.originalAddress);

    console.log('Sending batch to API:', {
      startIndex,
      endIndex,
      batchSize: batchAddresses.length
    });

    try {
      const response = await axios.post<NormalizationResponse>(API_ENDPOINTS.UPLOAD.NORMALIZE, {
        addresses: batchAddresses
      });

      if (response.data.queueId) {
        console.log('Received queue ID:', response.data.queueId);
        setCurrentBatchQueueId(response.data.queueId);
      } else {
        throw new Error('Invalid response format from normalization endpoint');
      }

      if (response.data.result) {
        setAddresses(prev => {
          const newAddresses = [...prev];
          response.data.result!.forEach((normAddr, idx) => {
            const globalIdx = startIndex + idx;
            if (newAddresses[globalIdx]) {
              newAddresses[globalIdx] = {
                ...newAddresses[globalIdx],
                ...normAddr
              };
            }
          });
          return newAddresses;
        });
      }
    } catch (error) {
      console.error('Error processing batch:', error);
      message.error('Có lỗi xảy ra khi chuẩn hóa địa chỉ. Vui lòng thử lại sau.');
      setIsNormalizing(false);
      setCurrentBatchIndex(0);
      setCurrentBatchQueueId(null);
      setIsProcessingBatch(false);
    }
  };

  // Effect to handle next batch processing
  useEffect(() => {
    if (isNormalizing && !isProcessingBatch && currentBatchIndex > 0 && currentBatchIndex < addresses.length) {
      console.log('Starting next batch after index update:', currentBatchIndex);
      processNextBatch();
    }
  }, [currentBatchIndex, isNormalizing, isProcessingBatch, addresses.length]);

  // Single polling effect for batch status
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkStatus = async () => {
      if (!currentBatchQueueId) {
        console.log('No queue ID available, skipping status check');
        return;
      }

      console.log('Checking status for queue:', currentBatchQueueId);

      try {
        const response = await axios.get<NormalizationStatus>(API_ENDPOINTS.UPLOAD.STATUS(currentBatchQueueId));
        const { status, result, error, userGreenCoins, userGoldCoins } = response.data;

        console.log('Status response:', {
          status,
          resultCount: result?.length,
          error,
          userGreenCoins,
          userGoldCoins
        });

        if (status === 'completed' && result && Array.isArray(result)) {
          // Update addresses by replacing the current batch
          setAddresses(prev => {
            const newAddresses = [...prev];
            const startIndex = currentBatchIndex;
            result.forEach((addr, index) => {
              newAddresses[startIndex + index] = addr;
            });
            return newAddresses;
          });

          const nextBatchIndex = currentBatchIndex + BATCH_SIZE;
          console.log('Batch completed, moving to next batch:', {
            currentBatchIndex,
            nextBatchIndex,
            totalAddresses: addresses.length
          });

          // Reset processing state and update batch index
          setIsProcessingBatch(false);
          setCurrentBatchQueueId(null);
          setCurrentBatchIndex(nextBatchIndex);
          
          if (userGreenCoins !== undefined && userGoldCoins !== undefined) {
            updateUserCoins(userGreenCoins, userGoldCoins);
          }

          // If we've processed all addresses, stop normalization
          if (nextBatchIndex >= addresses.length) {
            console.log('All batches completed, stopping normalization');
            setIsNormalizing(false);
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        } else if (status === 'failed') {
          console.log('Batch failed:', error);
          setError(error || 'Error normalizing addresses');
          setIsNormalizing(false);
          setIsProcessingBatch(false);
          setCurrentBatchQueueId(null);
          setCurrentBatchIndex(0);
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      } catch (error) {
        console.error('Error checking normalization status:', error);
        setError('Error checking normalization status');
        setIsNormalizing(false);
        setIsProcessingBatch(false);
        setCurrentBatchQueueId(null);
        setCurrentBatchIndex(0);
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    if (currentBatchQueueId) {
      console.log('Setting up polling for queue:', currentBatchQueueId);
      // Initial check
      checkStatus();
      // Set up polling interval only if we're still processing
      if (currentBatchIndex < addresses.length) {
        intervalId = setInterval(checkStatus, POLLING_INTERVAL);
        console.log('Polling interval set up');
      }
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        console.log('Cleaning up polling interval');
        clearInterval(intervalId);
      }
    };
  }, [currentBatchQueueId, currentBatchIndex, addresses.length, updateUserCoins]);

  const handleExportExcel = () => {
    if (addresses.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    try {
      // Prepare data for Excel
      const data = addresses.map((addr, index) => ({
        'STT': index + 1,
        'Địa chỉ gốc': addr.originalAddress,
        'Tỉnh/Thành phố': addr.city,
        'Quận/Huyện': addr.district,
        'Phường/Xã': addr.commune,
        'Tỉnh/Thành phố mới': addr.newCity || '',
        'Phường/Xã mới': addr.newCommune || '',
        'Khu vực giao hàng': (addr.city && addr.district) ? `${addr.city} - ${addr.district}` : ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(data);

      // Set column widths
      const colWidths = [
        { wch: 5 },  // STT
        { wch: 50 }, // Địa chỉ gốc
        { wch: 30 }, // Tỉnh/Thành phố
        { wch: 30 }, // Quận/Huyện
        { wch: 30 }, // Phường/Xã
        { wch: 30 }, // Tỉnh/Thành phố mới
        { wch: 30 }, // Phường/Xã mới
        { wch: 40 }  // Khu vực giao hàng
      ];
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Địa chỉ đã chuẩn hóa');

      // Generate Excel file
      const fileName = `dia-chi-da-chuan-hoa-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success('Xuất file Excel thành công');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất file Excel');
    }
  };

  const handleAddressUpdate = (index: number, updatedAddress: NormalizedAddress) => {
    setAddresses(prev => {
      const newAddresses = [...prev];
      newAddresses[index] = updatedAddress;
      return newAddresses;
    });
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header onExport={handleExportExcel} isNormalizing={isNormalizing} />
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Stack spacing={3}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              boxShadow: theme.shadows[2],
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom>
              Tải lên và chuẩn hóa địa chỉ
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0 }}>
              <ToggleButtonGroup
                value={inputMethod}
                exclusive
                onChange={handleInputMethodChange}
                aria-label="input method"
              >
                <ToggleButton value="file" aria-label="upload file">
                  <UploadIcon sx={{ mr: 1 }} />
                  Tải file Excel
                </ToggleButton>
                <ToggleButton value="text" aria-label="paste text">
                  <TextIcon sx={{ mr: 1 }} />
                  Nhập trực tiếp
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {inputMethod === 'file' ? (
              <FileUpload onUploadComplete={handleUploadComplete} />
            ) : (
              <TextInput onInputComplete={handleTextInputComplete} onTextChange={() => setIsReadyToNormalize(false)} />
            )}



            {addresses.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNormalizeClick}
                  disabled={isNormalizing || !user || user.greenCoins <= 0 || !isReadyToNormalize}
                  startIcon={isNormalizing ? <CircularProgress size={20} /> : <NormalizeIcon />}
                >
                  {isNormalizing 
                    ? (isProcessingBatch 
                        ? `Đang chuẩn hóa (${currentBatchIndex + 1}-${Math.min(currentBatchIndex + BATCH_SIZE, addresses.length)}/${addresses.length})...` 
                        : 'Đang chuẩn bị...')
                    : 'Chuẩn hóa địa chỉ'}
                </Button>
                {user && user.greenCoins <= 0 && (
                  <Typography variant="body2" color="error">
                    Bạn đã hết coin xanh để thực hiện chuẩn hóa. Vui lòng nạp thêm coins.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {addresses.length > 0 && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <AddressTable
                ref={addressTableRef}
                addresses={addresses}
                type="normalized"
                onAddressUpdate={handleAddressUpdate}
                onExport={handleExportExcel}
                isNormalizing={isNormalizing}
                isNormalizedDone={!isNormalizing && hasNormalized}
              />
            </Box>
          )}
        </Stack>
      </Container>

      {/* Add confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelNormalize}
        aria-labelledby="normalize-confirm-dialog-title"
        aria-describedby="normalize-confirm-dialog-description"
      >
        <DialogTitle id="normalize-confirm-dialog-title">
          Xác nhận chuẩn hóa địa chỉ
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="normalize-confirm-dialog-description">
            Dữ liệu trên bảng "Địa chỉ đã chuẩn hóa" sẽ bị xóa trước khi thực hiện chuẩn hóa mới. Bạn có chắc chắn muốn tiếp tục không?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNormalize} color="primary">
            Hủy
          </Button>
          <Button onClick={handleConfirmNormalize} color="primary" variant="contained" autoFocus>
            Tiếp tục
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment"
            element={
              <ProtectedRoute>
                <Payment />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
